import {
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { MenuProps, TableProps } from 'antd';
import { Button, Dropdown, Input, Radio, Switch, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useDeleteBlogMutation,
  useGetBlogsQuery,
  useUpdateBlogMutation,
} from '@/modules/blogs/blogsApi';
import type { BlogPost, BlogStatusFilter } from '@/types/blog';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { getMediaImageUrl } from '@/utils/media';
import { ucfirst } from '@/utils/string';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { useBlogQueryParams } from './hooks/useBlogQueryParams';
import styles from './Blogs.module.scss';

export default function Blogs() {
  const notify = useNotify();
  const navigate = useNavigate();
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } = useBlogQueryParams();
  const { data: listResult, isLoading, isFetching } = useGetBlogsQuery(apiParams);
  useGlobalLoader(isFetching && !!listResult);

  const posts = useMemo(() => listResult?.results ?? [], [listResult?.results]);
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const [searchDraft, setSearchDraft] = useState(state.search);
  const [statusDraft, setStatusDraft] = useState<BlogStatusFilter>(state.status);

  const [updateBlog] = useUpdateBlogMutation();
  const [deleteBlog] = useDeleteBlogMutation();

  const handleResetFiltersAndSort = () => {
    setSearchDraft('');
    setStatusDraft('all');
    resetAll();
  };

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const handlePublishedChange = useCallback(
    async (post: BlogPost, isPublished: boolean) => {
      const result = await resolveMutationResult(
        updateBlog({ id: post.id, body: { isPublished } }),
      );

      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        return;
      }

      notify.error(result.message);
    },
    [notify, updateBlog],
  );

  const handleDelete = useCallback(
    async (post: BlogPost) => {
      const confirmed = await confirmDelete(post.title);
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteBlog(post.id));
      if (result.ok) {
        notify.success(result.message ?? 'Blog post deleted.');
        return;
      }
      notify.error(result.message);
    },
    [deleteBlog, notify],
  );

  const getRowActionItems = useCallback(
    (post: BlogPost): MenuProps['items'] => [
      {
        key: 'preview',
        label: 'Preview',
        icon: <EyeOutlined />,
        onClick: () => navigate(`/blogs/${post.id}/preview`),
      },
      {
        key: 'edit',
        label: 'Edit',
        icon: <EditOutlined />,
        onClick: () => navigate(`/blogs/${post.id}/edit`),
      },
      { type: 'divider' },
      {
        key: 'delete',
        label: 'Delete',
        danger: true,
        onClick: () => {
          void handleDelete(post);
        },
      },
    ],
    [handleDelete, navigate],
  );

  const columns: ColumnsType<BlogPost> = useMemo(
    () => [
      {
        title: (
          <SortableColumnTitle
            label="TITLE"
            field="title"
            activeField={state.sortBy}
            sortOrder={state.sortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'title',
        key: 'title',
        className: styles.titleCol,
        filteredValue: state.search ? [state.search] : null,
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setSearchDraft('');
              patchParams({ search: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Search title or slug"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
            />
          </ColumnFilterPanel>
        ),
        render: (title: string, record) => (
          <Link to={`/blogs/${record.id}/preview`} className={styles.titleLink}>
            <div className={styles.titleRow}>
              <PreviewImage
                src={getMediaImageUrl(record.coverImage)}
                size={36}
                className={styles.thumb}
                fallback={ucfirst(title).charAt(0)}
                alt={title}
              />
              <div className={styles.titleBlock}>
                <span className={styles.titleCell}>{ucfirst(title)}</span>
                <span className={styles.slug}>{record.slug}</span>
              </div>
            </div>
          </Link>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="STATUS"
            field="isPublished"
            activeField={state.sortBy}
            sortOrder={state.sortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'isPublished',
        key: 'isPublished',
        align: 'center',
        filteredValue: state.status !== 'all' ? [state.status] : null,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { status: statusDraft === 'all' ? null : statusDraft },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setStatusDraft('all');
              patchParams({ status: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group
              value={statusDraft}
              onChange={(event) => setStatusDraft(event.target.value)}
            >
              <Radio value="all">All</Radio>
              <Radio value="published">Published</Radio>
              <Radio value="draft">Draft</Radio>
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setStatusDraft(state.status);
          },
        },
        render: (isPublished: boolean, record) => (
          <Switch
            checked={isPublished}
            size="small"
            onChange={(checked) => handlePublishedChange(record, checked)}
          />
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="UPDATED"
            field="updatedAt"
            activeField={state.sortBy}
            sortOrder={state.sortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (value?: string) => (
          <span className={styles.dateCell}>{value ? formatDateTime(value) : '—'}</span>
        ),
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_value, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']}>
            <Button
              type="text"
              size="small"
              icon={<EllipsisOutlined />}
              className={styles.actionBtn}
            />
          </Dropdown>
        ),
      },
    ],
    [
      getRowActionItems,
      handlePublishedChange,
      handleSort,
      patchParams,
      searchDraft,
      state.search,
      state.sortBy,
      state.sortOrder,
      state.status,
      statusDraft,
    ],
  );

  const handleTableChange: TableProps<BlogPost>['onChange'] = (pagination) => {
    patchParams({
      page: pagination.current ?? 1,
      limit: pagination.pageSize ?? pageSize,
    });
  };

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {hasActiveFiltersOrSort ? (
              <Button icon={<ReloadOutlined />} onClick={handleResetFiltersAndSort}>
                Reset
              </Button>
            ) : null}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(ROUTES.BLOG_CREATE)}
            >
              Add article
            </Button>
          </>
        }
      />

      <div className={styles.tableCard}>
        <Table<BlogPost>
          className={styles.table}
          rowKey="id"
          size="small"
          scroll={{ x: 'max-content' }}
          loading={isLoading && !listResult}
          columns={columns}
          dataSource={posts}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (totalCount, [start, end]) => `${start}-${end} of ${totalCount}`,
            size: 'small',
          }}
        />
      </div>
    </div>
  );
}
